import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Sun, Moon, LogOut, Settings, GitPullRequest } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

export function UserMenu() {
  const { user, accessInfo, signOut } = useAuth();
  const { themeName, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isCommand = themeName === 'command';
  const isAdmin = accessInfo?.accessLevel === 'admin';

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const getInitials = () => {
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.[0].toUpperCase() || '?';
  };

  const handleSignOut = async () => {
    setIsOpen(false);
    await signOut();
    navigate('/signin');
  };

  const handleAdmin = () => {
    setIsOpen(false);
    navigate('/admin');
  };

  const handleReviews = () => {
    setIsOpen(false);
    navigate('/reviews');
  };

  const handleSettings = () => {
    setIsOpen(false);
    navigate('/settings');
  };

  const handleThemeToggle = () => {
    toggleTheme();
    // Keep menu open after theme toggle
  };

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-8 h-8 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-surface-900 transition-all"
        title={user.email || 'User menu'}
      >
        {user.photoURL ? (
          <img
            src={user.photoURL}
            alt={user.displayName || 'User avatar'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white text-sm font-medium">
            {getInitials()}
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-surface-800 border border-surface-600 rounded-lg shadow-xl z-50 py-1 overflow-hidden">
          {/* Email Header */}
          <div className="px-4 py-3 border-b border-surface-600">
            <p className="text-sm font-medium text-text-primary truncate">
              {user.displayName || 'User'}
            </p>
            <p className="text-xs text-text-muted truncate">
              {user.email}
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {isAdmin && (
              <button
                onClick={handleAdmin}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-700 transition-colors"
              >
                <Shield className="w-4 h-4" />
                Admin
              </button>
            )}

            {isAdmin && (
              <button
                onClick={handleReviews}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-700 transition-colors"
              >
                <GitPullRequest className="w-4 h-4" />
                Reviews
              </button>
            )}

            <button
              onClick={handleSettings}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-700 transition-colors"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>

            <button
              onClick={handleThemeToggle}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-700 transition-colors"
            >
              {isCommand ? (
                <Sun className="w-4 h-4 text-warning-400" />
              ) : (
                <Moon className="w-4 h-4 text-primary-400" />
              )}
              {isCommand ? 'Light Mode' : 'Dark Mode'}
            </button>
          </div>

          {/* Sign Out */}
          <div className="border-t border-surface-600 py-1">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-error-400 hover:bg-surface-700 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
